import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@mui/styles';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
  IconButton,
  Box,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import { 
  downloadMarketDataPDF,
  previewMarketDataPDF,
  buildReportTitle,
} from 'utils/pdf-export';

const styles = (theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      maxWidth: '95vw',
      width: '95vw',
      height: '90vh',
      maxHeight: '90vh',
    },
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: theme.spacing(1),
  },
  titleText: {
    fontSize: 18,
    fontWeight: 500,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    height: 'calc(100% - 130px)',
    overflow: 'hidden',
  },
  settingsBar: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexWrap: 'wrap',
  },
  rowCountContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  rowCountInput: {
    width: 80,
  },
  titleFieldContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flex: 1,
    minWidth: 300,
  },
  titleField: {
    flex: 1,
    '& .MuiInputBase-input': {
      fontSize: 13,
    },
  },
  previewPanel: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.grey[100],
    overflow: 'hidden',
  },
  previewHeader: {
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.grey[200],
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  previewTitle: {
    fontSize: 12,
    fontWeight: 500,
    color: theme.palette.text.secondary,
  },
  previewFrame: {
    flex: 1,
    width: '100%',
    height: '100%',
    border: 'none',
    backgroundColor: '#525659',
  },
  previewLoading: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  previewEmpty: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: theme.palette.text.secondary,
  },
  actions: {
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  downloadButton: {
    minWidth: 140,
  },
});

// PDF Type configurations
const PDF_TYPES = {
  slideshow: {
    label: 'Slideshow',
    orientation: 'landscape',
    format: [16, 9],  // 16:9 aspect ratio (will be scaled)
    pageSize: [792, 445.5],  // 11" x 6.1875" (16:9 at 72dpi)
  },
  printable: {
    label: 'Printable',
    orientation: 'landscape',
    format: 'letter',
    pageSize: 'letter',
  },
};

class PDFExportDialog extends React.Component {
  constructor(props) {
    super(props);
    
    // Calculate initial max rows (can't use class method in constructor)
    let initialCount = props.totalCount;
    if (!initialCount || initialCount < 1) {
      initialCount = (props.chartData && props.chartData.length) || 200;
    }
    const initialMaxRows = Math.min(200, initialCount);
    
    this.state = {
      pdfType: 'printable',  // Default to printable
      customTitle: '',  // Will be computed when dialog opens
      customSubtitle: props.subtitle || '',  // Editable subtitle
      titleModified: false,  // Track if user has modified title
      options: {
        orientation: 'landscape',
        pageSize: 'letter',  // Printable uses letter
        includeHeader: true,
        includeFooter: true,
        includeChart: true,
        includeTable: false,
        maxRows: initialMaxRows,
        ...props.defaultOptions,
      },
      previewUrl: null,
      isGenerating: false,
      error: null,
    };
    
    this.generatePreviewTimer = null;
    this.titleChangeTimer = null;
  }

  /**
   * Compute the title that will be used in the PDF
   * Uses the same logic as the PDF generator
   */
  getComputedTitle = () => {
    const { title, reportType, filters, defaultOptions } = this.props;
    const { pdfType, options } = this.state;
    
    return buildReportTitle({
      title,
      reportType,
      filters,
      options: {
        ...options,
        ...defaultOptions,
        pdfType,
      },
    });
  };

  getValidMaxRows = (totalCount, chartData) => {
    // Get count from totalCount or chartData length, default to 200
    let count = totalCount;
    if (!count || count < 1) {
      count = (chartData && chartData.length) || 200;
    }
    return Math.min(200, count);
  };

  getTotalCount = () => {
    const { totalCount, chartData } = this.props;
    if (totalCount && totalCount > 0) {
      return totalCount;
    }
    return (chartData && chartData.length) || 0;
  };

  componentDidUpdate(prevProps, prevState) {
    const { open, chartData, totalCount, defaultOptions, reportType, subtitle } = this.props;
    const { options, pdfType, titleModified, customTitle, customSubtitle } = this.state;
    
    // Clear preview and reset when dialog opens fresh or reportType changes
    if ((open && !prevProps.open) || reportType !== prevProps.reportType) {
      // Clear old preview
      if (this.state.previewUrl) {
        URL.revokeObjectURL(this.state.previewUrl);
      }
      // Compute the title that will actually appear in the PDF
      const computedTitle = this.getComputedTitle();
      this.setState({ 
        previewUrl: null, 
        error: null,
        // Use computed title so it matches what the PDF shows
        customTitle: computedTitle,
        customSubtitle: subtitle || '',
        titleModified: false,
      });
    }
    
    // Update maxRows when totalCount or chartData changes
    if (totalCount !== prevProps.totalCount || 
        (chartData && chartData.length) !== (prevProps.chartData && prevProps.chartData.length)) {
      const newMaxRows = this.getValidMaxRows(totalCount, chartData);
      if (newMaxRows !== options.maxRows) {
        this.setState(prev => ({
          options: {
            ...prev.options,
            maxRows: newMaxRows,
            ...defaultOptions,
          },
        }));
      }
    }
    
    // Generate preview when dialog opens or any relevant option changes
    if (open && chartData && chartData.length > 0) {
      const optionsChanged = 
        pdfType !== prevState.pdfType ||
        options.maxRows !== prevState.options.maxRows;
      
      const dataChanged = 
        reportType !== prevProps.reportType ||
        chartData !== prevProps.chartData;
      
      // Also regenerate when title or subtitle changes (with debounce)
      const titleChanged = 
        customTitle !== prevState.customTitle ||
        customSubtitle !== prevState.customSubtitle;
      
      if ((open && !prevProps.open) || optionsChanged || dataChanged) {
        clearTimeout(this.generatePreviewTimer);
        this.generatePreviewTimer = setTimeout(() => this.generatePreview(), 300);
      } else if (titleChanged && titleModified) {
        // Debounce title/subtitle changes with a longer delay
        clearTimeout(this.titleChangeTimer);
        this.titleChangeTimer = setTimeout(() => this.generatePreview(), 800);
      }
    }
  }

  componentWillUnmount() {
    clearTimeout(this.generatePreviewTimer);
    clearTimeout(this.titleChangeTimer);
    if (this.state.previewUrl) {
      URL.revokeObjectURL(this.state.previewUrl);
    }
  }

  generatePreview = async () => {
    const { open, reportType, chartData, providerCounts, tableData, columns, filters, payors, defaultOptions } = this.props;
    const { options, pdfType, previewUrl, customTitle, customSubtitle } = this.state;
    
    if (!open) return;
    
    this.setState({ isGenerating: true, error: null });
    
    try {
      // Clean up previous preview URL
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }

      const pdfConfig = PDF_TYPES[pdfType];
      const pdfOptions = {
        ...options,
        ...defaultOptions,
        orientation: pdfConfig.orientation,
        pageSize: pdfConfig.pageSize,
        pdfType,  // Pass pdfType for style selection
      };

      const url = await previewMarketDataPDF({
        title: customTitle,  // Use the editable title
        subtitle: customSubtitle,  // Use the editable subtitle
        reportType,
        chartData,
        providerCounts,
        tableData,
        columns,
        filters,
        payors,
        options: pdfOptions,
      });

      this.setState({ previewUrl: url, isGenerating: false });
    } catch (err) {
      console.error('Failed to generate PDF preview:', err);
      this.setState({ 
        error: `Failed to generate PDF preview: ${err.message}`, 
        isGenerating: false,
        previewUrl: null,
      });
    }
  };

  handleDownload = async () => {
    const { reportType, chartData, providerCounts, tableData, columns, filters, payors, defaultOptions } = this.props;
    const { options, pdfType, customTitle, customSubtitle } = this.state;

    this.setState({ isGenerating: true, error: null });

    try {
      const pdfConfig = PDF_TYPES[pdfType];
      const pdfOptions = {
        ...options,
        ...defaultOptions,
        orientation: pdfConfig.orientation,
        pageSize: pdfConfig.pageSize,
        pdfType,  // Pass pdfType for style selection
      };

      // Build filename: Doctivity-[Tab]-[Market]-[Date w/ Time].pdf
      const tabNames = {
        'projected_patients': 'Projected-Patients',
        'captured_claims': 'Captured-Claims',
        'shared_affiliation': 'Shared-Affiliation',
        'payors': 'Payors',
        'payor_mix': 'Payors',
      };
      const tabName = tabNames[reportType] || 'Market-Data';
      const marketName = (filters?.market?.name || filters?.market?.label || filters?.market || 'All')
        .replace(/[^a-zA-Z0-9]/g, '-');
      const now = new Date();
      const dateTime = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}-${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
      const filename = `Doctivity-${tabName}-${marketName}-${dateTime}.pdf`;

      await downloadMarketDataPDF({
        title: customTitle,  // Use the editable title
        subtitle: customSubtitle,  // Use the editable subtitle
        reportType,
        chartData,
        providerCounts,
        tableData,
        columns,
        filters,
        payors,
        options: pdfOptions,
        filename,
      });
      
      this.setState({ isGenerating: false });
    } catch (err) {
      console.error('Failed to download PDF:', err);
      this.setState({ 
        error: `Failed to download PDF: ${err.message}`, 
        isGenerating: false,
      });
    }
  };

  handlePdfTypeChange = (newType) => {
    const pdfConfig = PDF_TYPES[newType];
    this.setState(prev => ({
      pdfType: newType,
      options: {
        ...prev.options,
        orientation: pdfConfig.orientation,
        pageSize: pdfConfig.pageSize,
      },
    }));
  };

  handleMaxRowsChange = (value) => {
    const maxRows = Math.min(200, Math.max(1, parseInt(value, 10) || 1));
    this.setState(prev => ({
      options: {
        ...prev.options,
        maxRows,
      },
    }));
  };

  handleTitleChange = (event) => {
    this.setState({
      customTitle: event.target.value,
      titleModified: true,
    });
  };

  handleSubtitleChange = (event) => {
    this.setState({
      customSubtitle: event.target.value,
      titleModified: true,
    });
  };

  handleRefresh = () => {
    // Regenerate preview
    this.generatePreview();
  };

  handleClose = () => {
    const { onClose } = this.props;
    if (onClose) {
      onClose();
    }
  };

  render() {
    const { classes, open, chartData } = this.props;
    const { pdfType, options, previewUrl, isGenerating, error, customTitle, customSubtitle } = this.state;
    const displayTotalCount = this.getTotalCount();

    return (
      <Dialog
        open={open}
        onClose={this.handleClose}
        className={classes.dialog}
        maxWidth={false}
      >
        <DialogTitle className={classes.dialogTitle}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Typography className={classes.titleText}>
              Export to PDF
            </Typography>
            <IconButton 
              onClick={this.handleRefresh} 
              size="small"
              disabled={isGenerating}
              title="Refresh preview"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Box>
          <IconButton onClick={this.handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent className={classes.content}>
          {/* Settings Bar at Top */}
          <div className={classes.settingsBar}>
            {/* Editable Title Field */}
            <Box className={classes.titleFieldContainer}>
              <Typography variant="body2" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                Title:
              </Typography>
              <TextField
                size="small"
                value={customTitle}
                onChange={this.handleTitleChange}
                className={classes.titleField}
                placeholder="Enter PDF title"
              />
            </Box>

            {/* Editable Subtitle Field */}
            <Box className={classes.titleFieldContainer}>
              <Typography variant="body2" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                Subtitle:
              </Typography>
              <TextField
                size="small"
                value={customSubtitle}
                onChange={this.handleSubtitleChange}
                className={classes.titleField}
                placeholder="Optional subtitle"
              />
            </Box>

            <Box className={classes.rowCountContainer}>
              <Typography variant="body2" style={{ fontSize: 13 }}>
                PDF Type:
              </Typography>
              <Select
                size="small"
                value={pdfType}
                onChange={(e) => this.handlePdfTypeChange(e.target.value)}
                style={{ minWidth: 160, fontSize: 13 }}
              >
                <MenuItem value="slideshow">Slideshow (16:9)</MenuItem>
                <MenuItem value="printable">Printable (Letter)</MenuItem>
              </Select>
            </Box>

            <Box className={classes.rowCountContainer}>
              <Typography variant="body2" style={{ fontSize: 13 }}>
                Max rows:
              </Typography>
              <TextField
                type="number"
                size="small"
                value={options.maxRows}
                onChange={(e) => this.handleMaxRowsChange(e.target.value)}
                inputProps={{ min: 1, max: 200 }}
                className={classes.rowCountInput}
              />
              <Typography variant="caption" color="textSecondary">
                / {displayTotalCount}
              </Typography>
            </Box>
          </div>

          {/* Preview Panel */}
          <div className={classes.previewPanel}>
            <div className={classes.previewHeader}>
              <Typography className={classes.previewTitle}>
                Preview
              </Typography>
            </div>

            {isGenerating ? (
              <div className={classes.previewLoading}>
                <CircularProgress size={40} />
                <Typography variant="body2" color="textSecondary">
                  Generating preview...
                </Typography>
              </div>
            ) : error ? (
              <div className={classes.previewEmpty}>
                <Typography color="error">{error}</Typography>
              </div>
            ) : previewUrl ? (
              <iframe
                src={previewUrl}
                className={classes.previewFrame}
                title="PDF Preview"
              />
            ) : (
              <div className={classes.previewEmpty}>
                <Typography variant="body2">
                  Click refresh to generate preview
                </Typography>
              </div>
            )}
          </div>
        </DialogContent>

        <DialogActions className={classes.actions}>
          <Button onClick={this.handleClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={this.handleDownload}
            disabled={isGenerating || !chartData || chartData.length === 0}
            startIcon={isGenerating ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
            className={classes.downloadButton}
          >
            {isGenerating ? 'Generating...' : 'Download PDF'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

PDFExportDialog.propTypes = {
  classes: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  reportType: PropTypes.string,
  chartData: PropTypes.array,
  providerCounts: PropTypes.object,
  tableData: PropTypes.array,
  columns: PropTypes.array,
  filters: PropTypes.object,
  payors: PropTypes.array,
  totalCount: PropTypes.number,
  defaultOptions: PropTypes.object,
};

PDFExportDialog.defaultProps = {
  title: 'Market Data Export',
  subtitle: '',
  reportType: 'projected_patients',
  chartData: [],
  providerCounts: null,
  tableData: [],
  columns: [],
  filters: {},
  payors: [],
  totalCount: 0,
  defaultOptions: {},
};

const StyledPDFExportDialog = withStyles(styles)(PDFExportDialog);

export { StyledPDFExportDialog as PDFExportDialog };
export default StyledPDFExportDialog;